import { useCallback, useRef, useState } from "react";
import axios from "axios";

/**
 * Picks the full-profile entry matching a lightweight list row. The detail
 * endpoint searches by name/email substring, so it may return several rows;
 * we prefer an exact id, then an exact email, then the first result.
 * @param {Array} list - Full-profile payloads from /search_profile
 * @param {Object} row - Lightweight list row {id, name, email}
 * @returns {Object|null} The matching full profile or null
 */
function pickExact(list, row) {
    if (!Array.isArray(list) || list.length === 0) return null;
    const byId = list.find((u) => u?.user_id === row.id);
    if (byId) return byId;
    const email = (row.email || "").toLowerCase();
    const byEmail = list.find((u) => (u?.my_profile?.email || "").toLowerCase() === email);
    return byEmail || list[0];
}

/**
 * Lazily loads a single user's FULL nested profile only when a row is selected,
 * caching the result by user id so re-selecting is instant. A latest-id guard
 * discards stale responses when the admin clicks rows quickly. This is what
 * keeps the page from ever fetching every user's heavy payload up front.
 * @returns {{ detail: Object|null, loading: boolean, error: string|null,
 *   loadDetail: Function, clear: Function }}
 */
function useUserDetail() {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const cacheRef = useRef(new Map());
    const latestIdRef = useRef(null);

    const clear = useCallback(() => {
        latestIdRef.current = null;
        setDetail(null);
        setError(null);
        setLoading(false);
    }, []);

    const loadDetail = useCallback(async (row) => {
        if (!row?.id || !row?.email) { clear(); return; }
        latestIdRef.current = row.id;
        const cached = cacheRef.current.get(row.id);
        if (cached) { setError(null); setLoading(false); setDetail(cached); return; }
        setLoading(true);
        setError(null);
        setDetail(null);
        try {
            const { data } = await axios.get(`/admin/users/search_profile/${encodeURIComponent(row.email)}`);
            if (latestIdRef.current !== row.id) return;
            const found = pickExact(data, row);
            if (found) cacheRef.current.set(row.id, found);
            setDetail(found);
            if (!found) setError("Utilisateur introuvable");
        } catch (err) {
            if (latestIdRef.current !== row.id) return;
            setError("Impossible de charger le profil");
            setDetail(null);
        } finally {
            if (latestIdRef.current === row.id) setLoading(false);
        }
    }, [clear]);

    return { detail, loading, error, loadDetail, clear };
}

export default useUserDetail;
